import { create } from 'zustand';
import { Category, VisibilityType } from '../types';
import { supabase } from '../config/supabase';

interface CategoryState {
  categories: Category[];
  isLoading: boolean;
  error: string | null;
  selectedCategory: Category | null;
  expandedCategories: string[];
  categoriesLoaded: boolean;
  activeCategory: Category | null;
  lastFetch: number;

  // Actions
  fetchCategories: () => Promise<void>;
  createCategory: (name: string, parentId?: string, options?: {
    visibilityType?: VisibilityType;
    isPublic?: boolean; // 保持向后兼容
    minPermissionLevel?: string;
    isOrderFolder?: boolean;
    orderNumber?: string | null;
    tenant?: 'cn' | 'intl'; // 新增：指定租户
  }) => Promise<Category>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  selectCategory: (category: Category | null) => void;
  toggleCategoryExpansion: (id: string) => void;
  moveCategory: (id: string, newParentId?: string) => Promise<void>;
  setActiveCategory: (category: Category | null) => void;
  clearCategories: () => void;
}

const buildCategoryTree = (categories: any[]): Category[] => {
  const childrenByParent = new Map<string | null, any[]>();
  const attachedIds = new Set<string>();

  for (const category of categories) {
    const parentId = category.parent_id || null;
    const siblings = childrenByParent.get(parentId) || [];
    siblings.push(category);
    childrenByParent.set(parentId, siblings);
  }

  const buildNode = (category: any, ancestors: Set<string>): Category | null => {
    if (ancestors.has(category.id)) {
      return null;
    }

    attachedIds.add(category.id);
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(category.id);

    const children = (childrenByParent.get(category.id) || [])
      .map((child) => buildNode(child, nextAncestors))
      .filter(Boolean) as Category[];

    return {
      ...category,
      children,
    };
  };

  const roots = (childrenByParent.get(null) || [])
    .map((category) => buildNode(category, new Set<string>()))
    .filter(Boolean) as Category[];

  const detachedRoots = categories
    .filter((category) => !attachedIds.has(category.id))
    .map((category) => buildNode(category, new Set<string>()))
    .filter(Boolean) as Category[];

  return [...roots, ...detachedRoots];
};

const validateCategoryMove = async (categoryId: string, newParentId?: string) => {
  if (!newParentId) return;

  if (categoryId === newParentId) {
    throw new Error('不能把文件夹移动到自己下面');
  }

  const visited = new Set<string>();
  let currentParentId: string | null | undefined = newParentId;

  while (currentParentId) {
    if (currentParentId === categoryId) {
      throw new Error('不能把文件夹移动到自己的子文件夹下面');
    }

    if (visited.has(currentParentId)) {
      throw new Error('目标文件夹父级关系异常，请先修复文件夹层级');
    }

    visited.add(currentParentId);

    const { data, error } = await supabase
      .from('categories')
      .select('id, parent_id')
      .eq('id', currentParentId)
      .single();

    if (error || !data) {
      throw new Error('目标文件夹不存在或无法访问');
    }

    currentParentId = data.parent_id;
  }
};

export const useCategoryStore = create<CategoryState>((set, get) => ({
  categories: [],
  isLoading: false,
  error: null,
  selectedCategory: null,
  expandedCategories: [],
  categoriesLoaded: false,
  lastFetch: 0,
  activeCategory: null,

  fetchCategories: async () => {
    try {
      // 立即加载分类，不做缓存检查
      set({ isLoading: true });

      // 获取当前用户信息
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('用户未登录');
      }

      // 获取用户详细信息（包括角色和租户）
      const { data: userProfile } = await supabase
        .from('v_fs_users')
        .select('role, tenant, can_access_all_tenants')
        .eq('id', user.id)
        .single();

      // Apple 模式：只加载分类元数据 + 文件计数
      // 文件详情将在进入文件夹时按需加载并永久缓存
      let query = supabase
        .from('categories')
        .select(`
          *,
          file_count:file_categories(count)
        `);

      // 租户过滤：超级管理员或有跨租户权限的用户可以看到所有租户的数据
      const canAccessAllTenants = userProfile?.can_access_all_tenants || userProfile?.role === 'super_admin';
      if (!canAccessAllTenants && userProfile?.tenant) {
        query = query.eq('tenant', userProfile.tenant);
      }

      // 应用权限过滤：显示用户自己创建的分类 或 其他用户创建的公开/受限公开分类（不包括私有）
      // 特殊处理：对于uploader角色，订单文件夹只显示自己创建的，但公开文件夹仍然可见
      if (userProfile?.role === 'uploader') {
        query = query.or(`created_by.eq.${user.id},and(created_by.neq.${user.id},or(is_public.eq.true,visibility_type.eq.public),is_order_folder.neq.true)`);
      } else {
        query = query.or(`created_by.eq.${user.id},and(created_by.neq.${user.id},or(is_public.eq.true,visibility_type.eq.public,visibility_type.eq.restricted_public))`);
      }

      const { data: categories, error } = await query.order('name');

      if (error) throw error;

      // Transform: 只包含计数，files 初始为空（将按需加载）
      const transformedCategories = (categories || []).map(category => ({
        ...category,
        file_count: category.file_count?.[0]?.count || 0,
        files: [] // 文件将在进入文件夹时按需加载
      }));

      const categoryTree = buildCategoryTree(transformedCategories);

      // Cache categories in localStorage
      try {
        localStorage.setItem('categories', JSON.stringify(categoryTree));
      } catch (e) {
        console.warn('Failed to cache categories:', e);
      }

      // 设置现在的时间为最后获取时间
      const now = Date.now();
      set({
        categories: categoryTree,
        lastFetch: now,
        categoriesLoaded: true
      });
    } catch (error) {
      console.error('Error fetching categories:', error);
      set({ error: (error as Error).message });
    } finally {
      set({ isLoading: false });
    }
  },

  createCategory: async (name: string, parentId?: string, options?: {
    visibilityType?: VisibilityType;
    isPublic?: boolean;
    minPermissionLevel?: string;
    isOrderFolder?: boolean;
    orderNumber?: string | null;
    tenant?: 'cn' | 'intl';
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // 获取用户的租户信息（用于默认值）
      const { data: userProfile } = await supabase
        .from('v_fs_users')
        .select('role, tenant, can_access_all_tenants')
        .eq('id', user.id)
        .single();

      // 确定租户：优先使用指定的租户，否则使用用户所属的租户，默认为 'cn'
      const finalTenant = options?.tenant || userProfile?.tenant || 'cn';

      // 确定可见性类型，优先使用新的 visibilityType，然后回退到 isPublic
      let finalVisibilityType: VisibilityType = 'private';
      if (options?.visibilityType) {
        finalVisibilityType = options.visibilityType;
      } else if (options?.isPublic) {
        finalVisibilityType = 'public';
      }

      // 为了向后兼容，同时设置 is_public 字段
      const isPublicValue = finalVisibilityType === 'public';

      const { data, error } = await supabase
        .from('categories')
        .insert({
          name,
          parent_id: parentId,
          created_by: user.id,
          file_count: 0,
          is_public: isPublicValue,
          visibility_type: finalVisibilityType,
          min_permission_level: (options?.minPermissionLevel as any) || 'basic',
          is_order_folder: !!options?.isOrderFolder,
          order_number: options?.orderNumber || null,
          tenant: finalTenant // 新增：设置租户
        })
        .select()
        .single();

      if (error) throw error;

      // userProfile 已在函数开头获取，可直接使用

      let refreshQuery = supabase
        .from('categories')
        .select(`
          *,
          files:file_categories(
            file:files(*)
          )
        `);

      // 租户过滤
      const canAccessAllTenants = userProfile?.can_access_all_tenants || userProfile?.role === 'super_admin';
      if (!canAccessAllTenants && userProfile?.tenant) {
        refreshQuery = refreshQuery.eq('tenant', userProfile.tenant);
      }

      // 根据用户角色应用不同的权限过滤
      if (userProfile?.role === 'uploader') {
        refreshQuery = refreshQuery.or(`created_by.eq.${user.id},and(created_by.neq.${user.id},or(is_public.eq.true,visibility_type.eq.public),is_order_folder.neq.true)`);
      } else {
        refreshQuery = refreshQuery.or(`created_by.eq.${user.id},and(created_by.neq.${user.id},or(is_public.eq.true,visibility_type.eq.public,visibility_type.eq.restricted_public))`);
      }

      const { data: categories } = await refreshQuery.order('name');

      const transformedCategories = (categories || []).map(category => ({
        ...category,
        files: category.files?.map((f: any) => ({
          ...f.file,
          createdAt: f.file.created_at,
          updatedAt: f.file.updated_at,
          createdBy: f.file.created_by,
          updatedBy: f.file.updated_by
        })) || []
      }));

      const categoryTree = buildCategoryTree(transformedCategories);

      // Update cache
      localStorage.setItem('categories', JSON.stringify(categoryTree));

      set({ categories: categoryTree });
      return data;
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  },

  updateCategory: async (id: string, updates: Partial<Category>) => {
    try {
      const { error } = await supabase
        .from('categories')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      // Refresh categories to get the updated tree
      await get().fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  },

  deleteCategory: async (id: string) => {
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Refresh categories to get the updated tree
      await get().fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  },

  selectCategory: (category) => set({ selectedCategory: category }),

  setActiveCategory: (category) => set({ activeCategory: category }),

  toggleCategoryExpansion: (id) => set((state) => ({
    expandedCategories: state.expandedCategories.includes(id)
      ? state.expandedCategories.filter(catId => catId !== id)
      : [...state.expandedCategories, id]
  })),

  moveCategory: async (id: string, newParentId?: string) => {
    try {
      await validateCategoryMove(id, newParentId);

      const { error } = await supabase
        .from('categories')
        .update({ parent_id: newParentId })
        .eq('id', id);

      if (error) throw error;

      // Refresh categories to get the updated tree
      await get().fetchCategories();
    } catch (error) {
      console.error('Error moving category:', error);
      throw error;
    }
  },

  clearCategories: () => set({
    categories: [],
    isLoading: false,
    error: null,
    selectedCategory: null,
    expandedCategories: [],
    categoriesLoaded: false,
    lastFetch: 0,
    activeCategory: null
  })
}));
